/**
 * GET /rest/my-history  vs  POST /jaxrs/my-history/{historyType}
 *
 * Two things had to be reconciled by hand; the generator pairs on path shape and would not have
 * found this one at all.
 *
 * 1. The Task API selects active-vs-completed with a `status` query parameter, where the Web API
 *    splits it into two paths (`/my-history/active`, `/my-history/completed`) - which is the form
 *    approval-web itself uses (`utils/api/api.js#getHistoryTasks`). One `historyType` parameter
 *    drives both: a path segment on one side, a query value on the other.
 *
 * 2. The Web side is a POST that reads. It is a search with a form-encoded body, not a write, so
 *    it is deliberately NOT marked `mutating` - running both sides is safe here. Do not take
 *    "POST" as meaning "changes data" when curating; the generator flags every POST and that
 *    guess is wrong for the search endpoints.
 *
 * The two filter sets barely overlap. Only the parameters both APIs honour are exposed, so the two
 * sides are always asked the same question; the Task API's `text`/`resolutionStatus`/`sortOrder` and
 * the Web API's dozen column filters are left out on purpose.
 */
import {PROCESS_STATUS_PAIRS} from "../decoders.js";

export const myHistory = {
    id: "my-history",
    label: "My history",
    summary: "Processes the logged-in user has already handled.",
    params: [
        {
            name: "historyType",
            label: "historyType",
            // a path segment on the Web side, so it is required
            in: "path",
            choices: ["active", "completed"],
            defaultValue: "completed",
            appliesTo: ["task", "web"],
            help: "Which half of the history to read. The Task API takes it as status=ACTIVE|COMPLETED, the Web API as a path segment.",
        },
        {
            name: "companyId",
            label: "companyId",
            in: "query",
            appliesTo: ["task", "web"],
            options: "companies",
            help: "Restrict to one company/tenant. A query parameter on both sides.",
        },
        {
            name: "page",
            label: "page",
            in: "query",
            defaultValue: 1,
            appliesTo: ["task", "web"],
            help: "1-based page number. Query on the Task API, form field on the Web API.",
        },
        {
            name: "rows",
            label: "rows",
            in: "query",
            defaultValue: 20,
            appliesTo: ["task", "web"],
            help: "Page size, max 100 on the Task API. Defaulted on both sides so the two pages line up.",
        },
    ],
    sides: {
        task: {
            method: "GET",
            path: "/my-history",
            operationId: "list",
            scope: "tasks:history-own",
            // `produces: ["application/json"]` only - unlike /my-tasks, the versioned
            // application/vnd.approval-platform-v1+json is rejected here with a 406
            accept: "application/json",
            query: params => ({
                status: params.historyType?.toUpperCase(),
                companyId: params.companyId,
                page: params.page,
                rows: params.rows,
            }),
            extract: body => (Array.isArray(body?.rows) ? body.rows : []),
            shape: "PaginatedListOfShortProcessDetails { rows, page, records }",
        },
        web: {
            method: "POST",
            path: "/my-history/{historyType}",
            operationId: "getActiveProcesses / getCompletedProcesses",
            scope: "session:read",
            // page and rows travel in the form body here, not the query string
            query: params => ({companyId: params.companyId}),
            body: params => ({page: params.page, rows: params.rows}),
            bodyType: "form",
            extract: body => (Array.isArray(body?.rows) ? body.rows : []),
            shape: "PaginatedListProcessTable { rows, page, records, userdata, version }",
        },
    },
    compare: {
        // ShortProcessDetails calls it uidProcess; ProcessTable calls it uid
        key: {task: "uidProcess", web: "uid"},
        aliases: [
            {task: "uidProcess", web: "uid"},
            {task: "uidDocument", web: "documentUid"},
            // confirmed against staging: both carried 4907006 for the same process
            {task: "externalDocumentId", web: "externalApplicationId"},
        ],
        // both Task API status fields compare against the one Web API integer; see decoders.js
        derived: [
            ...PROCESS_STATUS_PAIRS,
        ],
        notes: {
            received: "Task API gives a date only; the Web API keeps the time of day, so this loses precision",
            documentDueDate: "Web API returns Jackson's [year, month, day] array; the Task API an ISO date",
            lastChanged: "Task API renders UTC as a locale-formatted, zoneless string truncated to the minute (\"5/18/26 7:47 AM\"); the Web API returns epoch millis",
        },
    },
};
